import { useEffect, useState, useRef, useCallback } from 'react';
import { apiFetch, apiImageUrl } from '../../utils/streamApi';
import { makeSimpleRing } from '../../utils/polygon';
import IndustrialLayout, { toast } from './IndustrialLayout';
import { Field, sel } from '../../components/AnalyticsReport';

// ── Shop Analytics Config ─────────────────────────────────────────────────────
// Sets up the SCHEDULED analyzer: draw the door and the zones on a camera once,
// pick the hours to cover and the hour to run, and the report then refreshes
// itself nightly from the site's own recordings.
//
// Why it is scheduled and not live: measured, the analyzer runs at 1.24x
// realtime without shelf zones and 1.96x with them, at the 8 fps the counting
// needs to be correct. Nothing that reads a live stream can keep up, so the
// numbers come from recorded footage after the window closes. Live in/out
// counting is a different, far cheaper thing and is configured on the Object
// Intelligence page.
//
// Everything here is saved to detector_config.video_analytics on the camera,
// through the same PUT /cameras/{idx} every other detector config uses.

// Geometry, in drawing order. Same shapes and the same meanings as the upload
// analyzer — the backend hands them to the identical code.
const DRAW = [
  { key: 'entry_line', label: 'Counting line', shape: 'line', color: '#22c55e',
    hint: 'Two clicks. Put it ACROSS THE DOORWAY. This single choice decides whether the numbers are right: a line drawn just inside the shop misses real entries, and the report then says 1 in / 0 out on footage with two customers.' },
  { key: 'shop_zone', label: 'Shop floor', shape: 'poly', color: '#38bdf8',
    hint: 'Time spent is measured inside here. Keeps passers-by visible through the door out of the figures.' },
  { key: 'counter_zone', label: 'Behind counter', shape: 'poly', color: '#f59e0b',
    hint: 'The staff side. Anyone spending 3+ seconds here is staff for the whole window and never counts as a customer, and their time behind the counter is reported. Usually the only staff shape needed.' },
  { key: 'service_zone', label: 'At the counter', shape: 'poly', color: '#fb7185',
    hint: 'The customer side, where someone stands to be served. Gives time at the counter and how many were served.' },
  { key: 'staff_zones', label: 'Staff area (extra)', shape: 'poly', color: '#a855f7', multi: true,
    hint: 'Only for places staff go that are NOT the counter — a back room or dispensary. Do not draw it over floor a customer can stand on.' },
  { key: 'shelf_zones', label: 'Shelf', shape: 'poly', color: '#facc15', multi: true, named: true,
    hint: 'A reach is inferred from a hand entering the shelf or its contents changing — there is no sensor on the door, so this counts interactions. Draw it tight to the shelf face, never over open floor. Shelves cost about 35% more analysis time.' },
];

const DAYS = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];

const FPS_NOTE = {
  3: 'Keeps up easily, but MISCOUNTS — a track drifts onto another person and exits are missed. Measured: 2 in / 1 out where the truth was 2 in / 2 out. Not recommended.',
  5: 'A compromise, unmeasured. Between the rate that is known wrong and the rate that is known right.',
  8: 'The measured-correct rate. Costs about 1.24x the footage length, or 1.96x with shelf zones drawn.',
};

export default function IndustrialShopAnalyticsPage() {
  const [cameras, setCameras] = useState([]);
  const [scope, setScope]     = useState(null);
  const [cfg, setCfg]         = useState(null);
  const [dirty, setDirty]     = useState(false);
  const [saving, setSaving]   = useState(false);
  const [drawKey, setDrawKey] = useState('entry_line');
  const [pts, setPts]         = useState([]);
  const [imgUrl, setImgUrl]   = useState(null);
  const [imgErr, setImgErr]   = useState(false);
  const [state, setState]     = useState({});     // watermark per camera
  const canvasRef = useRef(null);
  const imgRef    = useRef(null);

  const active = DRAW.find(d => d.key === drawKey) || DRAW[0];

  const blank = () => ({
    enabled: false,
    window: { start: '10:00', end: '13:00' },
    run_at: '21:00',
    days: [0, 1, 2, 3, 4, 5, 6],
    report_retention_days: 90,
    proof_retention_days: 20,
    keep_video: false,
    params: { analysis_fps: 8, entry_line: null, shop_zone: null, counter_zone: null,
              service_zone: null, staff_zones: [], shelf_zones: [], detect_shelf: true },
  });

  // Normalise whatever is saved into the editor's shape. A camera that has never
  // been configured, and one saved by an older build with fewer keys, must both
  // land on a complete object or the inputs render uncontrolled.
  const normalize = (saved) => {
    const b = blank();
    const s = saved || {};
    return {
      ...b, ...s,
      window: { ...b.window, ...(s.window || {}) },
      days: Array.isArray(s.days) && s.days.length ? s.days : b.days,
      params: { ...b.params, ...(s.params || {}),
                staff_zones: Array.isArray(s.params?.staff_zones) ? s.params.staff_zones : [],
                shelf_zones: Array.isArray(s.params?.shelf_zones) ? s.params.shelf_zones : [] },
    };
  };

  useEffect(() => {
    apiFetch('/ui/cameras').then(r => r.json())
      .then(d => setCameras(d.cameras || [])).catch(() => {});
    apiFetch('/ui/analytics/cameras').then(r => r.json())
      .then(d => {
        const m = {};
        (d.cameras || []).forEach(c => { m[c.idx] = c; });
        setState(m);
      }).catch(() => {});
  }, []);

  useEffect(() => {
    if (!cameras.length || scope != null) return;
    setScope(cameras[0].idx);
  }, [cameras, scope]);

  useEffect(() => {
    if (scope == null) return;
    const cam = cameras.find(c => c.idx === scope);
    setCfg(normalize(cam?.detector_config?.video_analytics));
    setPts([]); setDirty(false); setDrawKey('entry_line');
    apiImageUrl(`/cameras/${scope}/frame`).then(u => { setImgUrl(u); setImgErr(false); })
      .catch(() => setImgUrl(null));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [scope, cameras]);

  const patch = (k, v) => { setCfg(c => ({ ...c, [k]: v })); setDirty(true); };
  const patchParam = (k, v) => {
    setCfg(c => ({ ...c, params: { ...c.params, [k]: v } })); setDirty(true);
  };

  // ── canvas ──────────────────────────────────────────────────────────────────
  const redraw = useCallback(() => {
    const cv = canvasRef.current, img = imgRef.current;
    if (!cv || !img || !img.naturalWidth) return;
    const W = img.clientWidth, H = img.clientHeight;
    cv.width = W; cv.height = H;
    const ctx = cv.getContext('2d');
    ctx.clearRect(0, 0, W, H);
    if (!cfg) return;

    const dot = (p, col) => {
      ctx.fillStyle = col; ctx.beginPath();
      ctx.arc(p[0] * W, p[1] * H, 4, 0, 7); ctx.fill();
    };
    const poly = (pp, col, label) => {
      if (!pp || pp.length < 3) return;
      ctx.strokeStyle = col; ctx.fillStyle = col + '33'; ctx.lineWidth = 2;
      ctx.beginPath();
      pp.forEach((p, i) => i ? ctx.lineTo(p[0] * W, p[1] * H) : ctx.moveTo(p[0] * W, p[1] * H));
      ctx.closePath(); ctx.fill(); ctx.stroke();
      pp.forEach(p => dot(p, col));
      if (label) {
        ctx.fillStyle = col; ctx.font = 'bold 12px sans-serif';
        ctx.fillText(label, pp[0][0] * W + 4, pp[0][1] * H - 4);
      }
    };

    const P = cfg.params || {};
    poly(P.shop_zone, '#38bdf8', 'shop floor');
    poly(P.counter_zone, '#f59e0b', 'behind counter');
    poly(P.service_zone, '#fb7185', 'at the counter');
    (P.staff_zones || []).forEach((z, i) => poly(z, '#a855f7', `staff ${i + 1}`));
    (P.shelf_zones || []).forEach((s, i) =>
      poly(s?.polygon || s, '#facc15', s?.name || `shelf ${i + 1}`));

    if (P.entry_line?.length === 2) {
      const [a, b] = P.entry_line;
      ctx.strokeStyle = '#22c55e'; ctx.lineWidth = 3;
      ctx.beginPath(); ctx.moveTo(a[0] * W, a[1] * H); ctx.lineTo(b[0] * W, b[1] * H); ctx.stroke();
      dot(a, '#22c55e'); dot(b, '#22c55e');
    }

    // in-progress clicks, with the closing edge dashed: drawn open, four points
    // read as a triangle and crossed edges stayed invisible until after saving.
    if (pts.length) {
      ctx.strokeStyle = '#f59e0b'; ctx.lineWidth = 2; ctx.beginPath();
      pts.forEach((p, i) => i ? ctx.lineTo(p[0] * W, p[1] * H) : ctx.moveTo(p[0] * W, p[1] * H));
      ctx.stroke();
      if (active.shape === 'poly' && pts.length >= 3) {
        ctx.save(); ctx.setLineDash([5, 4]); ctx.lineWidth = 1.5;
        ctx.beginPath();
        ctx.moveTo(pts[pts.length - 1][0] * W, pts[pts.length - 1][1] * H);
        ctx.lineTo(pts[0][0] * W, pts[0][1] * H);
        ctx.stroke(); ctx.restore();
      }
      pts.forEach((p, i) => {
        dot(p, '#f59e0b');
        if (active.shape !== 'poly') return;
        ctx.fillStyle = '#1f2937'; ctx.font = 'bold 10px sans-serif';
        ctx.fillText(String(i + 1), p[0] * W - 3, p[1] * H + 3.5);
      });
    }
  }, [cfg, pts, active.shape]);

  useEffect(() => { redraw(); }, [redraw, imgUrl]);
  useEffect(() => {
    const on = () => redraw();
    window.addEventListener('resize', on);
    return () => window.removeEventListener('resize', on);
  }, [redraw]);

  const onCanvasClick = (e) => {
    const cv = canvasRef.current; if (!cv) return;
    const r = cv.getBoundingClientRect();
    const p = [(e.clientX - r.left) / r.width, (e.clientY - r.top) / r.height];
    if (active.shape === 'line') {
      const next = [...pts, p];
      if (next.length === 2) { patchParam('entry_line', next); setPts([]); }
      else setPts(next);
      return;
    }
    setPts(prev => [...prev, p]);       // functional, so fast clicks are not dropped
  };

  const finishShape = () => {
    if (pts.length < 3) { toast('Click at least 3 points.', 'err'); return; }
    const { poly: ring, fixed } = makeSimpleRing(pts);
    if (fixed) toast('Those corners crossed over — reordered them into a clean shape.', 'warn');
    if (active.key === 'staff_zones') {
      patchParam('staff_zones', [...(cfg.params.staff_zones || []), ring]);
    } else if (active.key === 'shelf_zones') {
      const n = (cfg.params.shelf_zones || []).length + 1;
      patchParam('shelf_zones', [...(cfg.params.shelf_zones || []),
                                 { name: `Shelf ${n}`, polygon: ring }]);
    } else {
      patchParam(active.key, ring);
    }
    setPts([]);
  };

  const clearShape = () => {
    if (active.multi) patchParam(active.key, []);
    else patchParam(active.key, null);
    setPts([]);
  };

  const hasGeometry = () => {
    const P = cfg?.params || {};
    return !!(P.entry_line || P.shop_zone || P.counter_zone || P.service_zone
              || (P.staff_zones || []).length || (P.shelf_zones || []).length);
  };

  const save = async () => {
    if (scope == null || !cfg) return;
    if (cfg.enabled && !hasGeometry()) {
      toast('Draw the counting line, or at least one zone, before enabling.', 'err');
      return;
    }
    // Commit an uncommitted shape rather than losing it — the drawn-but-not-
    // finished trap that costs an installer a whole run.
    let params = { ...cfg.params };
    if (pts.length >= 3 && active.shape === 'poly') {
      const { poly: ring } = makeSimpleRing(pts);
      if (active.key === 'staff_zones') params.staff_zones = [...(params.staff_zones || []), ring];
      else if (active.key === 'shelf_zones') params.shelf_zones = [...(params.shelf_zones || []),
        { name: `Shelf ${(params.shelf_zones || []).length + 1}`, polygon: ring }];
      else params[active.key] = ring;
    }
    setSaving(true);
    try {
      const body = { detector_config: { video_analytics: { ...cfg, params } } };
      const r = await apiFetch(`/cameras/${scope}`, {
        method: 'PUT', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body),
      });
      if (!r.ok) throw new Error();
      setCfg(c => ({ ...c, params }));
      setPts([]); setDirty(false);
      toast(cfg.enabled
        ? `Saved — this camera will be analysed at ${cfg.run_at} for ${cfg.window.start}-${cfg.window.end}.`
        : 'Saved — analytics is off for this camera.', 'ok');
    } catch { toast('Save failed.', 'err'); }
    finally { setSaving(false); }
  };

  // `mins` runs the last N minutes instead of the configured window. Without it
  // the button is useless on the day of install: with an overnight window, "today's
  // window" at 4pm resolves to LAST night, which has no footage if recording was
  // only just switched on — so the installer presses it, gets "no recorded
  // segments", and has no way to test what they just drew.
  const runNow = async (mins) => {
    if (dirty) { toast('Save first — the run uses the saved geometry.', 'err'); return; }
    const body = { cam: scope };
    if (mins) {
      const fmt = (d) => `${d.getFullYear()}${String(d.getMonth() + 1).padStart(2, '0')}`
        + `${String(d.getDate()).padStart(2, '0')}_${String(d.getHours()).padStart(2, '0')}`
        + `${String(d.getMinutes()).padStart(2, '0')}${String(d.getSeconds()).padStart(2, '0')}`;
      // Stop a minute back: the newest segment is still being written, and a
      // half-written .ts is what makes a stitch fail for no visible reason.
      const end = new Date(Date.now() - 60 * 1000);
      body.to   = fmt(end);
      body.from = fmt(new Date(end.getTime() - mins * 60 * 1000));
    }
    try {
      const r = await apiFetch('/ui/analytics/run-now', {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body),
      });
      const d = await r.json();
      if (!r.ok) throw new Error(d?.detail || '');
      toast(mins
        ? `Started on the last ${mins} minutes. Watch the Video Analyzer page for progress.`
        : 'Started. It runs at full CPU — watch the Video Analyzer page for progress.', 'ok');
    } catch (e) { toast(`Could not start: ${e.message || 'failed'}`, 'err'); }
  };

  const st = state[scope] || {};
  const shelvesOn = (cfg?.params?.shelf_zones || []).length > 0;
  const winHours = (() => {
    if (!cfg) return 0;
    const [sh, sm] = (cfg.window.start || '0:0').split(':').map(Number);
    const [eh, em] = (cfg.window.end || '0:0').split(':').map(Number);
    let h = (eh + em / 60) - (sh + sm / 60);
    if (h <= 0) h += 24;
    return h;
  })();
  // Prefer what THIS camera actually cost last time. The fallback constants are
  // deliberately above the 1.24x/1.96x published figures: those were measured on
  // original mp4 clips, while a scheduled window is stitched from .ts segments,
  // which measured 1.43x and 1.56x on the same footage. Guessing low tells an
  // installer the job fits when it does not, and they find out a day later.
  const measured = st.last_realtime_factor || null;
  const factor   = measured || (shelvesOn ? 2.3 : 1.6);
  const costHours = winHours * factor;

  return (
    <IndustrialLayout title="Shop Analytics Config">
      <div className="ind-card">
        <span className="ind-eye">Camera</span>
        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginTop: 8 }}>
          {cameras.map(c => (
            <button key={c.idx} className={`ind-pill${scope === c.idx ? ' on' : ''}`}
              onClick={() => {
                if (dirty && !window.confirm('Discard unsaved changes?')) return;
                setScope(c.idx);
              }}>
              {c.name || `Camera ${c.idx}`}
              {state[c.idx]?.configured ? ' ✓' : ''}
            </button>
          ))}
          {!cameras.length && <span style={{ fontSize: 12, color: 'var(--ind-tx3)' }}>No cameras.</span>}
        </div>
      </div>

      {cfg && (
        <>
          <div className="ind-card">
            <div style={{ display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
              <span className="ind-eye">Scheduled analysis</span>
              <label style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 12 }}>
                <input type="checkbox" checked={!!cfg.enabled}
                  onChange={e => patch('enabled', e.target.checked)} />
                Analyse this camera on a schedule
              </label>
              <span style={{ flex: 1 }} />
              <button className="ind-pill" onClick={save} disabled={saving || !dirty}>
                {saving ? 'Saving…' : dirty ? '💾 Save' : 'Saved'}
              </button>
              <button className="ind-pill" onClick={() => runNow(15)} disabled={!cfg.enabled || dirty}>
                ▶ Test on the last 15 min
              </button>
              <button className="ind-pill" onClick={() => runNow(0)} disabled={!cfg.enabled || dirty}>
                Run today's window
              </button>
            </div>
            <p style={{ fontSize: 12, color: 'var(--ind-tx3)', margin: '10px 0 0', lineHeight: 1.65 }}>
              The report arrives <b>after</b> the run, not live — the analyzer needs
              about {shelvesOn ? '2' : '1.3'} hours of computing per hour of footage,
              so nothing reading a live stream could keep up. Live in / out counting
              is separate, real-time, and set up on the Object Intelligence page.
            </p>
            {st.last_window && (
              <p style={{ fontSize: 12, color: 'var(--ind-tx3)', margin: '6px 0 0' }}>
                Last analysed window: <code>{st.last_window}</code>
                {st.last_error ? <span style={{ color: 'var(--ind-warn, #f59e0b)' }}> — {st.last_error}</span> : ''}
              </p>
            )}
          </div>

          {/* ── geometry ─────────────────────────────────────────────────── */}
          <div className="ind-card">
            <span className="ind-eye">Draw the shop</span>
            <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', margin: '10px 0' }}>
              {DRAW.map(d => {
                const P = cfg.params || {};
                const set = d.multi ? (P[d.key] || []).length > 0 : !!P[d.key];
                return (
                  <button key={d.key} className={`ind-pill${drawKey === d.key ? ' on' : ''}`}
                    onClick={() => { setDrawKey(d.key); setPts([]); }}
                    style={{ borderColor: drawKey === d.key ? d.color : undefined }}>
                    <span style={{ color: d.color }}>●</span> {d.label}{set ? ' ✓' : ''}
                  </button>
                );
              })}
            </div>
            <p style={{ fontSize: 12, color: 'var(--ind-tx2)', lineHeight: 1.65, margin: '0 0 10px' }}>
              {active.hint}
            </p>

            <div style={{ position: 'relative', display: 'inline-block', maxWidth: '100%' }}>
              {imgUrl && !imgErr
                ? <img ref={imgRef} src={imgUrl} alt="" onLoad={redraw}
                    onError={() => setImgErr(true)}
                    style={{ maxWidth: '100%', borderRadius: 8, display: 'block' }} />
                : <div style={{ padding: 40, fontSize: 12, color: 'var(--ind-tx3)',
                    border: '1px dashed var(--ind-line)', borderRadius: 8 }}>
                    No frame from this camera — is it online?
                  </div>}
              {imgUrl && !imgErr &&
                <canvas ref={canvasRef} onClick={onCanvasClick}
                  style={{ position: 'absolute', inset: 0, cursor: 'crosshair' }} />}
            </div>

            <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', marginTop: 10 }}>
              {active.shape === 'poly' &&
                <button className="ind-pill" onClick={finishShape}>✓ Finish {active.label}</button>}
              <button className="ind-pill" onClick={clearShape}>Clear {active.label}</button>
              {!!pts.length && <button className="ind-pill" onClick={() => setPts([])}>Undo points</button>}
              <button className="ind-pill"
                onClick={() => apiImageUrl(`/cameras/${scope}/frame`).then(u => { setImgUrl(u); setImgErr(false); })}>
                ↻ Refresh frame
              </button>
            </div>

            {/* named shelves get a rename + remove row each */}
            {active.key === 'shelf_zones' && (cfg.params.shelf_zones || []).length > 0 && (
              <div style={{ marginTop: 12, display: 'flex', flexDirection: 'column', gap: 6 }}>
                {(cfg.params.shelf_zones || []).map((s, i) => (
                  <div key={i} style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
                    <input value={s.name || ''} style={{ ...sel, width: 180 }}
                      onChange={e => {
                        const next = [...cfg.params.shelf_zones];
                        next[i] = { ...next[i], name: e.target.value };
                        patchParam('shelf_zones', next);
                      }} />
                    <button className="ind-pill"
                      onClick={() => patchParam('shelf_zones',
                        cfg.params.shelf_zones.filter((_, j) => j !== i))}>Remove</button>
                  </div>
                ))}
              </div>
            )}
            {active.key === 'staff_zones' && (cfg.params.staff_zones || []).length > 0 && (
              <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', marginTop: 12 }}>
                {(cfg.params.staff_zones || []).map((_z, i) => (
                  <span key={i} className="ind-pill">
                    Staff {i + 1}
                    <button onClick={() => patchParam('staff_zones',
                      cfg.params.staff_zones.filter((_, j) => j !== i))}
                      style={{ background: 'none', border: 'none', color: 'inherit',
                        cursor: 'pointer', marginLeft: 6 }}>×</button>
                  </span>
                ))}
              </div>
            )}
          </div>

          {/* ── schedule ─────────────────────────────────────────────────── */}
          <div className="ind-card">
            <span className="ind-eye">When</span>
            <div style={{ display: 'flex', gap: 18, flexWrap: 'wrap', marginTop: 10 }}>
              <Field label="Count these hours"
                hint="The footage to analyse. An overnight span (22:00 to 02:00) is understood.">
                <div style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
                  <input type="time" value={cfg.window.start} style={sel}
                    onChange={e => patch('window', { ...cfg.window, start: e.target.value })} />
                  <span style={{ fontSize: 12, color: 'var(--ind-tx3)' }}>to</span>
                  <input type="time" value={cfg.window.end} style={sel}
                    onChange={e => patch('window', { ...cfg.window, end: e.target.value })} />
                </div>
              </Field>
              <Field label="Run the analysis at"
                hint="Deliberately separate from the hours above. Running at window close would load every core while the shop is busy and live detection needs them.">
                <input type="time" value={cfg.run_at} style={sel}
                  onChange={e => patch('run_at', e.target.value)} />
              </Field>
              <Field label="Frames per second">
                <select value={cfg.params.analysis_fps} style={sel}
                  onChange={e => patchParam('analysis_fps', Number(e.target.value))}>
                  <option value={3}>3 — fast, but miscounts</option>
                  <option value={5}>5 — compromise</option>
                  <option value={8}>8 — measured correct</option>
                </select>
              </Field>
            </div>
            <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', marginTop: 12 }}>
              {DAYS.map((d, i) => (
                <button key={d} className={`ind-pill${(cfg.days || []).includes(i) ? ' on' : ''}`}
                  onClick={() => {
                    const cur = cfg.days || [];
                    patch('days', cur.includes(i) ? cur.filter(x => x !== i) : [...cur, i].sort());
                  }}>{d}</button>
              ))}
            </div>
            <p style={{ fontSize: 12, color: 'var(--ind-tx2)', marginTop: 12, lineHeight: 1.65 }}>
              {FPS_NOTE[cfg.params.analysis_fps] || ''}
            </p>
            <p style={{ fontSize: 12, marginTop: 8, lineHeight: 1.65,
              color: costHours >= winHours || costHours > 10
                ? 'var(--ind-warn, #f59e0b)' : 'var(--ind-tx3)' }}>
              {winHours.toFixed(1)} h of footage {shelvesOn ? 'with shelves ' : ''}
              will take roughly <b>{costHours.toFixed(1)} h</b> to analyse
              {measured
                ? <> — at {measured}×, which is what this camera actually took last time.</>
                : <> — estimated at {factor}×, until this camera has run once and knows its own figure.</>}
              {costHours + winHours > 24 &&
                ' That does not fit in a day alongside the window itself: the report would fall further behind every night. Shorten the hours, or drop the shelf zones.'}
            </p>
          </div>

          {/* ── retention ────────────────────────────────────────────────── */}
          <div className="ind-card">
            <span className="ind-eye">How long to keep it</span>
            <div style={{ display: 'flex', gap: 18, flexWrap: 'wrap', marginTop: 10 }}>
              <Field label="Keep the numbers" hint="The report rows. A few KB a day.">
                <input type="number" min={1} max={400} value={cfg.report_retention_days} style={{ ...sel, width: 90 }}
                  onChange={e => patch('report_retention_days', Math.max(1, Number(e.target.value) || 90))} />
              </Field>
              <Field label="Keep the proof photos"
                hint="The evidence behind each figure. Deleted on this clock; the numbers above outlive them.">
                <input type="number" min={1} max={400} value={cfg.proof_retention_days} style={{ ...sel, width: 90 }}
                  onChange={e => patch('proof_retention_days', Math.max(1, Number(e.target.value) || 20))} />
              </Field>
              <Field label="Keep the stitched video"
                hint="Off by default. It is ~0.7 GB per camera-hour of footage already on disk as recordings — keeping it stores a second copy.">
                <label style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 12 }}>
                  <input type="checkbox" checked={!!cfg.keep_video}
                    onChange={e => patch('keep_video', e.target.checked)} />
                  Keep it after analysis
                </label>
              </Field>
            </div>
          </div>
        </>
      )}
    </IndustrialLayout>
  );
}
